import { z } from 'zod';
import { DaysForm } from './schedule';
import * as yup from 'yup';
import { SchedulerEventModel } from '@bryntum/schedulerpro';

export const EventSchema = z.object({
  id: z.string().uuid(),
  externalId: z.string().uuid(),
  driveStartTime: z.string(),
  homeArrivalTime: z.string(),
  resourceId: z.string().uuid(),
  tenantId: z.string(),
  taskReferenceId: z.string(),
  source: z.string(),
  eventName: z.string(),
  startDateTime: z.string(),
  endDateTime: z.string(),
  insuredName: z.string(),
  claimNumber: z.string(),
  isTask: z.boolean(),
  isRescheduleAllowed: z.boolean(),
  peril: z.string(),
  timeZoneId: z.string(),
  lossLat: z.number(),
  lossLong: z.number(),
  notes: z.string().optional(),
  type: z.string(),
  locationData: z
    .object({
      locationType: z.string().optional(),
      geoJson: z.object({
        type: z.string().optional(),
        geometry: z.object({
          type: z.string().optional(),
          coordinates: z.array(z.number()),
        }),
        properties: z.object({
          postalCode: z.string(),
        }),
      }),
    })
    .optional(),
  isForced: z.boolean().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  editedBy: z.string().optional().nullable(),
});

export type EventSource = 'scheduler' | 'customer';

export type Event = z.infer<typeof EventSchema>;

export type UpdateEventAssignmentAndTimeParams = {
  oldResourceId: string;
  newResourceId: string;
  eventId: string;
  startDateTime: Date | string;
  endDateTime: Date | string;
  reasonCode: string;
  event?: unknown;
};

export type LocationData = {
  locationType: string;
  geoJson: GeoJson;
};

export type GeoJson = {
  type?: string;
  geometry?: Geometry;
  properties?: GeoJsonProperties;
};

export type Geometry = {
  type: string;
  coordinates: number[];
};

export type GeoJsonProperties = {
  postalCode: string;
};

export interface SchedulerEventForCopy extends Partial<SchedulerEventModel> {
  id: string;
  type: string;
  startDate: string;
  endDate: string;
  originalData: {
    geoStatus: string;
    name: string;
    isTask: boolean;
    targetQueue: string;
    methodOfInspection: string;
    insuredName: string;
    lossCity: string;
    lossState: string;
    lossCounty: string;
    lossZip: string;
    assignmentType: string;
    mustStartAfter: string;
    mustEndBefore: string;
    isForced: boolean;
  };
}

export type UpsertEventParams = {
  resourceId: string;
  externalId: string;
  tenantId: string;
  eventName: string;
  source: EventSource;
  startDateTime: string;
  endDateTime: string;
  timeZoneId?: string;
  TaskReferenceId: string; //this should be removed from schema
  notes?: string | null;
  category?: string | null;
  locationData?: LocationData;
  isForced?: boolean;
  createdBy?: string;
  editedBy?: string;
  isAllDay?: boolean;
};

export interface EditEventForm {
  startDateTime: Date;
  endDateTime: Date;
  name: string;
  repeat: boolean;
  repeatFor: number;
  timeZoneId: string;
  days: DaysForm;
  eventId?: string;
  category?: string;
  postalCode?: string;
  isForced?: boolean;
  createdBy?: string;
  editedBy?: string;
  isAllDay?: boolean;
}

const startDate = new Date();
const endDate = new Date();
startDate.setSeconds(0);
startDate.setMilliseconds(0);
endDate.setSeconds(0);
endDate.setMilliseconds(0);

export const editEventFormDefault: EditEventForm = {
  startDateTime: startDate,
  endDateTime: endDate,
  name: '',
  repeatFor: 0,
  repeat: false,
  timeZoneId: '',
  postalCode: '',
  days: {
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
  },
  isForced: false,
  isAllDay: false,
};

export type DrageOrResizeParams = {
  eventId: string;
  eventName: string;
  oldResourceId: string;
  newResourceId: string;
  startTime: Date;
  endTime: Date;
  reasonCode: string;
  isPhantom: boolean | undefined;
  data?: Event;
  type?: string;
  resourceId?: string;
  timeZoneId?: string;
  notes?: string;
  category?: string;
  postalCode?: string;
};

export type MappedDropEvent = {
  oldResourceId: string;
  newResourceId: string;
  eventId: string;
  eventName: string;
  startDate: Date;
  endDate: Date;
  isPhantom?: boolean;
  eventData?: Event;
  type?: string;
};

export const editEventFormSchema = yup.object().shape({
  startDateTime: yup.date().required('Start Date Time required'),
  endDateTime: yup
    .date()
    .test(
      'min_end_date',
      'End date time must be after than start date time',
      (value, context) => {
        const { startDateTime } = context.parent;

        if (!startDateTime || !value) {
          return false;
        }

        return value >= startDateTime;
      }
    )
    .when('isAllDay', {
      is: false,
      then: yup
        .date()
        .test(
          'min_end_date',
          'End date time must be after than start date time',
          (value, context) => {
            const { startDateTime } = context.parent;

            if (!startDateTime || !value) {
              return false;
            }

            return value > startDateTime;
          }
        ),
    })
    .required('End Date Time is required'),
  name: yup.string().required('Name is required'),
  postalCode: yup.string().optional(),
  repeat: yup.boolean().when('postalCode', {
    is: (postalCode: string) => postalCode,
    then: yup.boolean().isFalse(),
  }),
  category: yup.string().required('Category is required'),
  repeatFor: yup.number().when('repeat', {
    is: true,
    then: yup
      .number()
      .min(1, 'Repeated Days Must Be Greater Than 0')
      .max(365, 'Repeated Days Must Be Less Than or Equal to 365')
      .required('Number of days required')
      .transform((value) => (value ? value : 0)),
  }),
  timeZoneId: yup.string().required('Time zone is required'),
  isForced: yup.boolean().optional().nullable(),
  days: yup
    .object()
    .shape({
      sunday: yup.boolean(),
      monday: yup.boolean(),
      tuesday: yup.boolean(),
      wednesday: yup.boolean(),
      thursday: yup.boolean(),
      friday: yup.boolean(),
      saturday: yup.boolean(),
    })
    .when('repeat', {
      is: true,
      then: yup
        .object()
        .shape({
          sunday: yup.boolean(),
          monday: yup.boolean(),
          tuesday: yup.boolean(),
          wednesday: yup.boolean(),
          thursday: yup.boolean(),
          friday: yup.boolean(),
          saturday: yup.boolean(),
        })
        .test(
          'day_selected',
          'A repeated day must be selected',
          (value) =>
            !!value.sunday ||
            !!value.monday ||
            !!value.tuesday ||
            !!value.wednesday ||
            !!value.thursday ||
            !!value.friday ||
            !!value.saturday
        ),
    }),
});
